/**
 * Phone countries — the one list behind every phone field (board PB-66).
 *
 * A record stores the number (`phoneNumber`, "+1 (647) 555-0100") and the
 * two-letter ISO country it was entered with (`phoneCountry`, "CA"). The dial
 * code and label come from this list. Storing the country matters because
 * several countries share a dial code (US and CA are both +1), so the number
 * alone can't tell them apart.
 *
 * Shared by the browser and the Cloud Functions (future text messages). Pure
 * TS: no Firebase, `window`, `import.meta` or `process`. The browser imports it
 * as `@shared/phoneCountries`.
 */

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2, the stored value. */
  iso: string
  dialCode: string
  /** Short label shown in the picker. */
  label: string
}

export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  { iso: 'US', dialCode: '+1', label: 'US' },
  { iso: 'CA', dialCode: '+1', label: 'CA' },
  { iso: 'GB', dialCode: '+44', label: 'UK' },
  { iso: 'AU', dialCode: '+61', label: 'AU' },
  { iso: 'MX', dialCode: '+52', label: 'MX' },
  { iso: 'IE', dialCode: '+353', label: 'IE' },
  { iso: 'FR', dialCode: '+33', label: 'FR' },
  { iso: 'DE', dialCode: '+49', label: 'DE' },
  { iso: 'ES', dialCode: '+34', label: 'ES' },
  { iso: 'IT', dialCode: '+39', label: 'IT' },
  { iso: 'BR', dialCode: '+55', label: 'BR' },
  { iso: 'IN', dialCode: '+91', label: 'IN' },
  { iso: 'CN', dialCode: '+86', label: 'CN' },
  { iso: 'JP', dialCode: '+81', label: 'JP' },
  { iso: 'KR', dialCode: '+82', label: 'KR' },
  { iso: 'NZ', dialCode: '+64', label: 'NZ' },
]

export const DEFAULT_PHONE_COUNTRY = 'US'

/**
 * The flag emoji for an ISO code ("CA" → 🇨🇦): each letter maps to a regional
 * indicator symbol. Windows draws these as plain letters.
 */
export function flagEmoji(iso: string): string {
  return String.fromCodePoint(
    ...iso
      .toUpperCase()
      .split('')
      .map(c => 0x1f1e6 + c.charCodeAt(0) - 65)
  )
}

export function phoneCountryByIso(
  iso: string | null | undefined
): PhoneCountry | undefined {
  return iso ? PHONE_COUNTRIES.find(c => c.iso === iso) : undefined
}

/**
 * The country for a stored number: the saved `phoneCountry` when it matches
 * the number's dial code, otherwise the first country with that dial code
 * (numbers saved before PB-66 have no country).
 */
export function resolvePhoneCountry(
  dialCode: string,
  iso?: string | null
): PhoneCountry {
  const saved = phoneCountryByIso(iso)
  if (saved && saved.dialCode === dialCode) return saved
  return (
    PHONE_COUNTRIES.find(c => c.dialCode === dialCode) ??
    (phoneCountryByIso(DEFAULT_PHONE_COUNTRY) as PhoneCountry)
  )
}
